import React, { FC, useState } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { store, persistor } from './store/store';
import { DirectusAuthProvider } from './providers/DirectusAuthProvider';
import { APIAuthProvider } from './providers/APIAuthProvider';
import { TelegramWebAppProvider } from './providers/TelegramWebAppProvider';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { AppRouter } from './components/AppRouter';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { NetworkRecoveryManager } from './components/NetworkRecoveryManager';
import { AuthStateManager } from './components/auth/AuthStateManager';
import { AuthTokenValidator } from './components/auth/AuthTokenValidator';
import { Toaster } from './components/ui/sonner';
import { TelegramBackButtonManager } from './components/TelegramBackButtonManager';
// import { TelegramDebugPanel } from './components/debug/TelegramDebugPanel';

// Enhanced error logging for translation issues
if (process.env.NODE_ENV === 'development') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes('translation') ||
        event.reason?.message?.includes('lang_')) {
      console.error('📦 [App] Unhandled translation-related promise rejection:', event.reason);
    }
  });

  window.addEventListener('error', (event) => {
    if (event.error?.message?.includes('translation') ||
        event.error?.message?.includes('lang_')) {
      console.error('📦 [App] Unhandled translation-related error:', event.error);
    }
  });
}

const LoadingScreen: FC = () => {
  const [loadingTimeout, setLoadingTimeout] = React.useState(false);

  // Use hardcoded text to avoid circular dependency with translation system
  // The translation system is still loading, so we can't use useLanguage() hook here
  if (process.env.NODE_ENV === 'development') {
    console.log(`📦 [App] LoadingScreen - Showing static loading screen (no translation access to avoid circular dependency)`);
  }

  // Set a timeout to detect slow loading
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoadingTimeout(true);
      console.warn('📦 [App] LoadingScreen - App initialization taking longer than expected');
    }, 10000); // 10 second timeout

    return () => clearTimeout(timer);
  }, []);

  // Use static loading messages to avoid circular dependency
  const getLoadingMessage = () => {
    return {
      primary: loadingTimeout
        ? 'Loading taking longer than expected...'
        : 'Initializing application...',
      secondary: loadingTimeout
        ? 'Please check your connection and try refreshing if the issue persists.'
        : 'Please wait while we prepare everything for you'
    };
  };

  const messages = getLoadingMessage();

  return (
    <div className="flex h-screen bg-background">
      <div className="m-auto p-8 text-center max-w-md">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-foreground font-medium mb-2">{messages.primary}</p>
        <p className="text-muted-foreground text-sm mb-4">{messages.secondary}</p>

        {loadingTimeout && (
          <div className="space-y-3">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Taking longer than usual? Try refreshing the page.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 text-xs text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline"
              >
                Refresh Now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Enhanced Translation Loading Guard component with better isolation
const TranslationGuard: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [, setGuardTimeout] = React.useState(false);
  const [startTime] = React.useState(Date.now());
  React.useState(false);
  const [errorGracePeriod, setErrorGracePeriod] = React.useState(false);
  const [retryAttempt, setRetryAttempt] = React.useState(0);

  // Safely access language context with error boundary protection
  let translationsLoading = true;
  let language = 'en';
  let contextError: string | null = null;

  try {
    const context = useLanguage();
    translationsLoading = context.isLoading;
    language = context.language;

    // Only call getTranslationStatus if context is fully ready
    if (!context.isLoading && context.isInitialized) {
      try {
        const status = context.getTranslationStatus();
        contextError = status.error;
      } catch (error) {
        console.warn(`📦 [App] TranslationGuard - Error getting translation status:`, error);
        contextError = 'Failed to get translation status';
      }
    }
  } catch (error) {
    console.warn(`📦 [App] TranslationGuard - Error accessing language context:`, error);
    const errorMessage = error instanceof Error ? error.message : String(error);

    // Check for specific temporal dead zone error
    if (errorMessage.includes('before initialization') ||
        errorMessage.includes("Cannot access 't'")) {
      contextError = 'Temporal dead zone - language context initializing';
    } else {
      contextError = 'Language context initialization error';
    }

    translationsLoading = true; // Stay in loading state if context access fails
  }

  // Log translation context state for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log(`📦 [App] TranslationGuard - Language context status:`, {
      translationsLoading,
      currentLanguage: language,
      contextError,
      timestamp: new Date().toISOString(),
      loadingDuration: `${Date.now() - startTime}ms`
    });
  }

  // Set a timeout for translation loading
  React.useEffect(() => {
    if (translationsLoading && !contextError) {
      const timer = setTimeout(() => {
        console.warn(`📦 [App] TranslationGuard - Translation loading timeout for '${language}'`);
        setGuardTimeout(true);
      }, 15000); // 15 second timeout

      return () => clearTimeout(timer);
    } else {
      setGuardTimeout(false);
    }
  }, [translationsLoading, language, contextError]);

  // Set up a retry mechanism for temporal dead zone errors (moved to top level)
  React.useEffect(() => {
    if (contextError && contextError.includes('Temporal dead zone') && retryAttempt < 3) {
      const retryTimeout = setTimeout(() => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`📦 [App] TranslationGuard - Retrying context access (attempt ${retryAttempt + 1})`);
        }
        setRetryAttempt(prev => prev + 1);
      }, 1000 + (retryAttempt * 500)); // Progressive backoff

      return () => clearTimeout(retryTimeout);
    }
  }, [contextError, retryAttempt]);

  // Grace period for transient errors (moved to top level)
  React.useEffect(() => {
    if (contextError && !contextError.includes('initialization') && !errorGracePeriod) {
      setErrorGracePeriod(true);
      const graceTimer = setTimeout(() => {
        // After grace period, check if error persists
        if (contextError && retryAttempt < 2) {
          // Allow retry
          setRetryAttempt(prev => prev + 1);
          setErrorGracePeriod(false);
        } else if (contextError) {
          // Max retries reached, throw error
          throw new Error(`Translation loading failed after retries: ${contextError}`);
        }
      }, 3000); // 3 second grace period

      return () => clearTimeout(graceTimer);
    }
  }, [contextError, errorGracePeriod, retryAttempt]);

  // Handle context initialization errors with enhanced retry logic
  if (contextError && (contextError.includes('initialization') || contextError.includes('Temporal dead zone'))) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📦 [App] TranslationGuard - Context initialization error, showing loading screen with retry logic`);
    }

    return <LoadingScreen />;
  }

  // Wait for translations to load before rendering children
  if (translationsLoading) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`📦 [App] TranslationGuard - Still loading translations for '${language}', showing loading screen`);
    }
    return <LoadingScreen />;
  }

  // Handle other translation errors with grace period and retry mechanism
  if (contextError && !contextError.includes('initialization')) {
    console.warn(`📦 [App] TranslationGuard - Translation error detected:`, contextError);

    // Show loading screen with retry option during grace period
    return (
      <div className="flex h-screen bg-background">
        <div className="m-auto p-8 text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground font-medium mb-2">Initializing translations...</p>
          <p className="text-muted-foreground text-sm mb-4">
            {retryAttempt > 0 ? `Retry attempt ${retryAttempt}/2` : 'Please wait while we load the translation system'}
          </p>

          {retryAttempt > 0 && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
              <p className="text-xs text-amber-600 dark:text-amber-400 mb-2">
                Translation loading issue detected. Retrying...
              </p>
              <button
                onClick={() => {
                  setRetryAttempt(0);
                  setErrorGracePeriod(false);
                  window.location.reload();
                }}
                className="text-xs text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline"
              >
                Force Refresh
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (process.env.NODE_ENV === 'development') {
    console.log(`📦 [App] TranslationGuard - Translations loaded for '${language}', rendering children`);
  }
  return <>{children}</>;
};

// Enhanced Translation Error Boundary with better error recovery
class TranslationErrorBoundary extends React.Component<
  { children: React.ReactNode },
  {
    hasError: boolean;
    error?: Error;
    errorDetails?: string;
    detectedLanguage?: string;
    retryCount: number;
    showDetails: boolean;
  }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error: Error) {
    console.error('📦 [App] Translation context error caught:', error);

    // Try to extract more details about the error
    let errorDetails = error.message;
    let detectedLanguage = 'unknown';

    // Check for language-specific errors in the error message or stack
    if (error.message.includes('lang_id.json')) {
      detectedLanguage = 'Indonesian (ID)';
    } else if (error.message.includes('lang_en.json')) {
      detectedLanguage = 'English (EN)';
    } else if (error.message.includes('interaction_modal')) {
      errorDetails += ' (Related to interaction modal structure)';
    }

    // Check local storage for current language
    try {
      const savedLang = localStorage.getItem('app-language');
      if (savedLang) {
        detectedLanguage = savedLang === 'id' ? 'Indonesian (ID)' : 'English (EN)';
      }
    } catch (e) {
      console.warn('📦 [App] Could not read language from localStorage:', e);
    }

    return {
      hasError: true,
      error,
      errorDetails,
      detectedLanguage
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('📦 [App] Translation error boundary triggered:', {
      error: error.message,
      stack: error.stack,
      errorInfo,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Try to get additional context from window if available
    if (typeof window !== 'undefined' && (window as any).__languageContext) {
      const langContext = (window as any).__languageContext;
      console.error('📦 [App] Language context debug info:', {
        status: langContext.getStatus ? langContext.getStatus() : 'No status available',
        report: langContext.exportReport ? langContext.exportReport() : 'No report available'
      });
    }
  }

  handleClearCacheAndRetry = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📦 [App] Clearing translation cache and retrying...');
    }

    this.setState({ retryCount: this.state.retryCount + 1 });

    // Clear localStorage cache
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.includes('translation') || key.includes('language') || key.includes('cache')) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      console.warn('📦 [App] Could not clear localStorage cache:', e);
    }

    // Try to clear translation cache via window context
    if (typeof window !== 'undefined' && (window as any).__languageContext) {
      try {
        (window as any).__languageContext.clearCache();
      } catch (e) {
        console.warn('📦 [App] Could not clear language context cache:', e);
      }
    }

    // Reset error state and retry
    this.setState({ hasError: false, error: undefined, errorDetails: undefined });

    // Force a small delay before retry
    setTimeout(() => {
      if (this.state.hasError) {
        window.location.reload();
      }
    }, 500);
  };

  handleSwitchToEnglish = () => {
    if (process.env.NODE_ENV === 'development') {
      console.log('📦 [App] Switching to English fallback language...');
    }

    try {
      localStorage.setItem('app-language', 'en');
      this.setState({ hasError: false, error: undefined, errorDetails: undefined });

      // Force reload with English
      setTimeout(() => {
        window.location.reload();
      }, 200);
    } catch (e) {
      console.error('📦 [App] Failed to switch to English:', e);
      window.location.reload();
    }
  };

  handleToggleDetails = () => {
    this.setState({ showDetails: !this.state.showDetails });
  };

  render() {
    if (this.state.hasError) {
      const { error, errorDetails, detectedLanguage, retryCount, showDetails } = this.state;
      const isMaxRetries = retryCount >= 3;

      return (
        <div className="flex h-screen bg-background">
          <div className="m-auto p-8 text-center max-w-lg">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
              {/* Header */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">
                  Translation System Error
                </h2>
              </div>

              {/* Error Message */}
              <div className="text-left mb-4">
                <p className="text-sm text-red-600 dark:text-red-400 mb-2">
                  <strong>Issue:</strong> Failed to initialize the language system.
                </p>
                {detectedLanguage && (
                  <p className="text-sm text-red-600 dark:text-red-400 mb-2">
                    <strong>Language:</strong> {detectedLanguage}
                  </p>
                )}
                {retryCount > 0 && (
                  <p className="text-xs text-red-500 dark:text-red-400 mb-2">
                    <strong>Retry attempts:</strong> {retryCount}/3
                  </p>
                )}
              </div>

              {/* Suggested Actions */}
              <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-3 mb-4 text-left">
                <p className="text-xs font-medium text-red-700 dark:text-red-300 mb-2">Suggested Actions:</p>
                <ul className="text-xs text-red-600 dark:text-red-400 space-y-1">
                  <li>• Try clearing the cache and retrying</li>
                  <li>• Switch to English as a fallback language</li>
                  <li>• Check your internet connection</li>
                  <li>• Refresh the page if issues persist</li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {!isMaxRetries && (
                    <button
                      onClick={this.handleClearCacheAndRetry}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
                    >
                      🔄 Clear Cache & Retry
                    </button>
                  )}
                  <button
                    onClick={this.handleSwitchToEnglish}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                  >
                    🇬🇧 Switch to English
                  </button>
                </div>

                <button
                  onClick={() => window.location.reload()}
                  className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  🔃 Reload Application
                </button>
              </div>

              {/* Debug Information Toggle */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-4">
                  <button
                    onClick={this.handleToggleDetails}
                    className="text-xs text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 underline"
                  >
                    {showDetails ? 'Hide' : 'Show'} Technical Details
                  </button>

                  {showDetails && (
                    <div className="mt-2 p-2 bg-red-200/50 dark:bg-red-900/50 rounded text-left">
                      <pre className="text-xs text-red-700 dark:text-red-300 whitespace-pre-wrap font-mono">
                        <strong>Error:</strong> {error?.message}\n
                        {errorDetails && (
                          <><strong>Details:</strong> {errorDetails}\n\n</>
                        )}

                        <strong>Language:</strong> {detectedLanguage}\n
                        <strong>Timestamp:</strong> {new Date().toISOString()}\n
                        {error?.stack && (
                          <><strong>Stack:</strong>\n{error.stack.split('\n').slice(0, 5).join('\n')}</>
                        )}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Help Text */}
              <div className="mt-4 pt-3 border-t border-red-200 dark:border-red-700">
                <p className="text-xs text-red-500 dark:text-red-400">
                  If this problem persists, please contact support or try using a different browser.
                </p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const App: FC = () => {
  const [isValidationComplete, setIsValidationComplete] = useState(false);
  const [appStartTime] = React.useState(Date.now());

  if (process.env.NODE_ENV === 'development') {
    console.log(`📦 [App] App component rendering, validation complete: ${isValidationComplete}`);
  }

  // Log app initialization metrics
  React.useEffect(() => {
    if (isValidationComplete && process.env.NODE_ENV === 'development') {
      const initTime = Date.now() - appStartTime;
      console.log(`📦 [App] App initialization completed in ${initTime}ms`);
    }
  }, [isValidationComplete, appStartTime]);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <TranslationErrorBoundary>
          <LanguageProvider>
            <TranslationGuard>
              <ThemeProvider>
                <TelegramWebAppProvider>
                  <DirectusAuthProvider>
                    <APIAuthProvider>
                      <AuthStateManager>
                        <AuthTokenValidator
                          onValidationComplete={() => {
                            if (process.env.NODE_ENV === 'development') {
                              console.log(`📦 [App] Validation completed, rendering main application`);
                            }
                            setIsValidationComplete(true);
                          }}
                        >
                          <ErrorBoundary>
                            <NetworkRecoveryManager />
                            <div className="App min-h-screen font-sans antialiased screen-safe-container">
                              {isValidationComplete ? (
                                <>
                                  <TelegramBackButtonManager />
                                  <AppRouter />
                                </>
                              ) : (
                                <LoadingScreen />
                              )}
                              {/* <TelegramDebugPanel /> */}
                            </div>
                            <Toaster />
                          </ErrorBoundary>
                        </AuthTokenValidator>
                      </AuthStateManager>
                    </APIAuthProvider>
                  </DirectusAuthProvider>
                </TelegramWebAppProvider>
              </ThemeProvider>
            </TranslationGuard>
          </LanguageProvider>
        </TranslationErrorBoundary>
      </PersistGate>
    </Provider>
  );
};

export default App;

// Export components for testing
export { TranslationErrorBoundary, TranslationGuard, LoadingScreen };